import { useState } from 'react';
import { primaryRendition, trackBitrate, type Track, type TrackStatus } from '../lib/api';
import { formatBytes, formatDuration } from '../lib/format';

const STATUS_LABEL: Record<TrackStatus, string> = {
  uploading: 'Uploading',
  transcoding: 'Transcoding',
  ready: 'Ready',
  failed: 'Failed',
};

type Props = {
  tracks: Track[];
  /** 专辑当前的码率设置，用来标出哪些曲目还是旧档 */
  albumBitrate: number;
  onRename: (trackId: string, patch: { name?: string; artist?: string }) => void;
  onReorder: (order: string[]) => void;
  onRetranscode: (trackId: string) => void;
  onDelete: (track: Track) => void;
  onPreview: (track: Track) => void;
};

export function TrackList({
  tracks, albumBitrate, onRename, onReorder, onRetranscode, onDelete, onPreview,
}: Props) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const drop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) return;
    const next = [...tracks];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    onReorder(next.map((t) => t.trackId));
    setDragIndex(null);
    setOverIndex(null);
  };

  if (!tracks.length) {
    return <p className="hint">No tracks in this album yet. Drop audio files below to upload.</p>;
  }

  return (
    <>
      <div className="track-head">
        <span />
        <span className="num">#</span>
        <span>Title</span>
        <span>Artist</span>
        <span className="right">Length</span>
        <span className="right">xHE-AAC</span>
        <span>Status</span>
        <span />
      </div>

      <div className="track-rows">
        {tracks.map((track, index) => {
          const kbps = trackBitrate(track);
          const stale = kbps !== undefined && kbps !== albumBitrate;
          return (
          <div
            key={track.trackId}
            className={[
              'track-row',
              dragIndex === index ? 'dragging' : '',
              overIndex === index ? 'drag-over' : '',
            ].filter(Boolean).join(' ')}
            onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
            onDragLeave={() => setOverIndex((i) => (i === index ? null : i))}
            onDrop={(e) => { e.preventDefault(); drop(index); }}
          >
            <div
              className="handle"
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              title="Drag to reorder"
            >
              ⠿
            </div>

            <div className="track-num">{index + 1}</div>

            <div className="track-meta">
              <input
                className="cell-input"
                defaultValue={track.name}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && value !== track.name) onRename(track.trackId, { name: value });
                }}
              />
            </div>

            <div className="track-meta">
              <input
                className="cell-input"
                defaultValue={track.artist}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && value !== track.artist) onRename(track.trackId, { artist: value });
                }}
              />
            </div>

            <div className="track-dur">{formatDuration(track.durationMs)}</div>
            <div className="track-size">
              {formatBytes(primaryRendition(track)?.bytes)}
              {kbps !== undefined && (
                <em
                  className={stale ? 'stale' : ''}
                  title={stale
                    ? `The album is set to ${albumBitrate}k. Re-encode to catch up.`
                    : undefined}
                >
                  {kbps}k
                </em>
              )}
            </div>

            <div>
              <span className={`badge ${track.status}`}>
                <span className={`dot ${track.status === 'transcoding' ? 'pulse' : ''}`} />
                {STATUS_LABEL[track.status]}
              </span>
            </div>

            <div className="track-actions">
              <button
                className="ghost tiny"
                disabled={track.status !== 'ready'}
                onClick={() => onPreview(track)}
              >
                Play
              </button>
              <button
                className={`ghost tiny ${stale ? 'nudge' : ''}`}
                disabled={track.status === 'transcoding'}
                onClick={() => onRetranscode(track.trackId)}
                title={`Re-encode from the same master at ${albumBitrate}k`}
              >
                Re-encode
              </button>
              <button className="ghost tiny danger" onClick={() => onDelete(track)}>Delete</button>
            </div>

            {track.errorMessage && <div className="track-error">{track.errorMessage}</div>}
          </div>
          );
        })}
      </div>
    </>
  );
}
